import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AtencionClienteDashboardActions } from './atencion-cliente-dashboard.actions';
import { CoordinacionService } from '../../services/coordinacion/coordinacion.service';
import {
  ITabCounts,
  IOcrStats,
  IStatesPiechart,
  IContactsLinechart,
} from '../../interfaces/coordinacion.interface';

export interface AtencionClienteDashboardStateModel {
  loadingTopSection: boolean;
  loadingBottomSection: boolean;
  error: any | null;
  tabCounts: ITabCounts | null;
  ocrStats: IOcrStats | null;
  statesPiechart: IStatesPiechart | null;
  contactsLinechart: IContactsLinechart | null;
}

@State<AtencionClienteDashboardStateModel>({
  name: 'atencionClienteDashboard',
  defaults: {
    loadingTopSection: false,
    loadingBottomSection: false,
    error: null,
    tabCounts: null,
    ocrStats: null,
    statesPiechart: null,
    contactsLinechart: null,
  },
})
@Injectable()
export class AtencionClienteDashboardState {
  constructor(private coordSvc: CoordinacionService) {}

  @Selector()
  static getLoadingTopSection(state: AtencionClienteDashboardStateModel): boolean {
    return state.loadingTopSection;
  }

  @Selector()
  static getLoadingBottomSection(state: AtencionClienteDashboardStateModel): boolean {
    return state.loadingBottomSection;
  }

  @Selector()
  static getTabCounts(state: AtencionClienteDashboardStateModel): ITabCounts | null {
    return state.tabCounts;
  }

  @Selector()
  static getOcrStats(state: AtencionClienteDashboardStateModel): IOcrStats | null {
    return state.ocrStats;
  }

  @Selector()
  static getStatesPiechart(state: AtencionClienteDashboardStateModel): IStatesPiechart | null {
    return state.statesPiechart;
  }

  @Selector()
  static getContactsLinechart(state: AtencionClienteDashboardStateModel): IContactsLinechart | null {
    return state.contactsLinechart;
  }

  @Selector()
  static getError(state: AtencionClienteDashboardStateModel): any | null {
    return state.error;
  }

  @Action(AtencionClienteDashboardActions.GetTabCounts)
  getTabCounts(ctx: StateContext<AtencionClienteDashboardStateModel>) {
    return this.coordSvc.getTabCounts().pipe(
      tap((res) => ctx.patchState({ tabCounts: res.data ?? null })),
      catchError((error) => {
        ctx.dispatch(new AtencionClienteDashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      })
    );
  }

  @Action(AtencionClienteDashboardActions.GetOcrStats)
  getOcrStats(ctx: StateContext<AtencionClienteDashboardStateModel>) {
    return this.coordSvc.getOcrStats().pipe(
      tap((res) => ctx.patchState({ ocrStats: res.data ?? null })),
      catchError((error) => {
        ctx.dispatch(new AtencionClienteDashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      })
    );
  }

  @Action(AtencionClienteDashboardActions.GetStatesPiechart)
  getStatesPiechart(ctx: StateContext<AtencionClienteDashboardStateModel>) {
    ctx.patchState({ loadingTopSection: true, error: null });
    return this.coordSvc.getStatesPiechart().pipe(
      tap((res) =>
        ctx.patchState({ loadingTopSection: false, statesPiechart: res.data ?? null }),
      ),
      catchError((error) => {
        ctx.dispatch(new AtencionClienteDashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      }),
    );
  }

  @Action(AtencionClienteDashboardActions.GetContactsLinechart)
  getContactsLinechart(
    ctx: StateContext<AtencionClienteDashboardStateModel>,
    action: AtencionClienteDashboardActions.GetContactsLinechart,
  ) {
    ctx.patchState({ loadingBottomSection: true, error: null });
    return this.coordSvc.getContactsLinechart(action.startDate, action.endDate).pipe(
      tap((res) =>
        ctx.patchState({ loadingBottomSection: false, contactsLinechart: res.data ?? null }),
      ),
      catchError((error) => {
        ctx.dispatch(new AtencionClienteDashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      }),
    );
  }

  @Action(AtencionClienteDashboardActions.ErrorDashboard)
  errorDashboard(
    ctx: StateContext<AtencionClienteDashboardStateModel>,
    action: AtencionClienteDashboardActions.ErrorDashboard,
  ) {
    ctx.patchState({
      loadingTopSection: false,
      loadingBottomSection: false,
      error: action.error,
    });
  }
}
